// Per-set artist-name autofit. Each word stacked, all uniform-sized to the
// max where the visually-widest word fits the column AND total height fits
// the block. Same algorithm we proved out in the Python pipeline.

const FONT_PRELOADS = [
  "500 30px Orbitron",
  "600 30px Orbitron",
  "700 30px Orbitron",
  "800 30px Orbitron",
  "900 30px Orbitron",
  "400 22px Inter",
  "500 22px Inter",
  "600 22px Inter",
  "700 22px Inter",
  "800 22px Inter",
  "900 22px Inter",
  "500 22px 'JetBrains Mono'",
  "700 22px 'JetBrains Mono'",
];

export async function preloadFonts(): Promise<void> {
  await Promise.all(FONT_PRELOADS.map(spec => document.fonts.load(spec)));
  await document.fonts.ready;
}

export function applyAutofit(root: HTMLElement): void {
  // ARTIST NAMES — uniform sizing per block.
  root.querySelectorAll<HTMLElement>(".set").forEach(block => {
    const artist = block.querySelector<HTMLElement>(".artist");
    if (!artist) return;
    const time = block.querySelector<HTMLElement>(".time");
    const badge = block.querySelector<HTMLElement>(".badge");
    const padTop = 4, padBottom = 4, gap = 3;
    const timeH = time ? time.getBoundingClientRect().height : 0;
    const badgeH = badge ? 16 : 0;
    const innerH = block.clientHeight - timeH - gap - padTop - padBottom - badgeH;
    const innerW = block.clientWidth - 16;

    const isHeadliner = block.classList.contains("headliner");
    const isMain = block.classList.contains("main");
    const minSize = 9;
    const maxSize = isHeadliner ? 64 : (isMain ? 44 : 32);
    const lineHF = 1.04;

    // Idempotent: stash original text on first call so re-runs don't lose
    // the spaces between words after we tokenize into <span>s.
    let original = artist.dataset.originalText;
    if (!original) {
      original = (artist.textContent || "").trim().replace(/\s+/g, " ");
      artist.dataset.originalText = original;
    }
    const words = original.split(" ").filter(w => w.length > 0);
    if (words.length === 0) return;

    artist.innerHTML = words
      .map(w => `<span class="word">${escapeHTML(w)}</span>`)
      .join("");
    artist.style.lineHeight = "1.0";
    const wordEls = artist.querySelectorAll<HTMLElement>(".word");

    // Probe each word at 40px to find the visually-widest.
    wordEls.forEach(w => { w.style.fontSize = "40px"; });
    let widestEl = wordEls[0];
    wordEls.forEach(w => {
      if (w.scrollWidth > widestEl.scrollWidth) widestEl = w;
    });

    let lo = minSize, hi = maxSize, widthFit = minSize;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      widestEl.style.fontSize = mid + "px";
      if (widestEl.scrollWidth <= innerW) { widthFit = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }
    const heightFit = Math.floor(innerH / (words.length * lineHF));
    const finalSize = Math.max(minSize, Math.min(widthFit, heightFit, maxSize));
    wordEls.forEach(w => { w.style.fontSize = finalSize + "px"; });
  });

  // STAGE COLUMN HEADERS — same uniform-size approach.
  root.querySelectorAll<HTMLElement>(".stage-head").forEach(head => {
    const wordEls = head.querySelectorAll<HTMLElement>(".stage-word");
    if (!wordEls.length) return;
    const innerW = head.clientWidth - 12;
    const innerH = head.clientHeight - 8;
    const minSize = 14, maxSize = 64;
    const lineHF = 1.05;

    wordEls.forEach(w => { w.style.fontSize = "40px"; });
    let widestEl = wordEls[0];
    wordEls.forEach(w => {
      if (w.scrollWidth > widestEl.scrollWidth) widestEl = w;
    });

    let lo = minSize, hi = maxSize, widthFit = minSize;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      widestEl.style.fontSize = mid + "px";
      if (widestEl.scrollWidth <= innerW) { widthFit = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }
    const heightFit = Math.floor(innerH / (wordEls.length * lineHF));
    const finalSize = Math.max(minSize, Math.min(widthFit, heightFit, maxSize));
    wordEls.forEach(w => { w.style.fontSize = finalSize + "px"; });
  });

  // DAY NAME — fit to width
  const dayName = root.querySelector<HTMLElement>(".day-title .name");
  if (dayName) {
    const maxW = (root.clientWidth || parseInt(root.style.width)) - 80;
    dayName.style.display = "inline-block";
    dayName.style.lineHeight = "1";
    let lo = 60, hi = 110, best = 60;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      dayName.style.fontSize = mid + "px";
      const r = dayName.getBoundingClientRect();
      if (dayName.scrollWidth <= maxW && r.height <= 100) { best = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }
    dayName.style.fontSize = best + "px";
  }
}

function escapeHTML(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
